import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ArrowLeft, Play, Music2, Drum, Guitar, Piano, Loader2, Clock, Trophy, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';

const TRACKS = [
  { key: 'piano', name: 'Piano', icon: Piano, color: 'from-amber-500 to-orange-600', desc: '88 keys. Infinite songs.' },
  { key: 'guitar', name: 'Guitar', icon: Guitar, color: 'from-rose-500 to-pink-600', desc: 'Chords, strumming, songs.' },
  { key: 'drums', name: 'Drums', icon: Drum, color: 'from-emerald-500 to-teal-600', desc: 'Keep the beat alive.' },
  { key: 'bass', name: 'Bass', icon: Music2, color: 'from-indigo-500 to-purple-600', desc: 'The groove machine.' },
];

const LEVEL_BADGE = {
  1: { label: 'Beginner', color: 'bg-emerald-100 text-emerald-700' },
  2: { label: 'Easy', color: 'bg-blue-100 text-blue-700' },
  3: { label: 'Intermediate', color: 'bg-amber-100 text-amber-700' },
  4: { label: 'Advanced', color: 'bg-rose-100 text-rose-700' },
};

const MusicPage = () => {
  const { childId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [child, setChild] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTrack, setActiveTrack] = useState('piano');
  const [playingLesson, setPlayingLesson] = useState(null);

  useEffect(() => {
    fetchData();
  }, [childId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [childRes, lessonsRes, progressRes] = await Promise.all([
        api.get(`/children/${childId}`),
        api.get(`/activities?subject=music`),
        api.get(`/children/${childId}/progress`).catch(() => ({ data: { activities_completed: [] } })),
      ]);
      setChild(childRes.data);
      setLessons(lessonsRes.data || []);
      const completed = (progressRes.data?.activities_completed || []).reduce((acc, id) => {
        acc[id] = true;
        return acc;
      }, {});
      setProgress(completed);
    } catch (err) {
      toast.error('Failed to load music lessons');
    } finally {
      setLoading(false);
    }
  };

  const markComplete = async (lessonId) => {
    try {
      await api.post(`/activities/${lessonId}/complete?child_id=${childId}&score=100&time_spent=600`);
      setProgress((prev) => ({ ...prev, [lessonId]: true }));
      toast.success('Lesson completed! +XP & coins earned 🎉');
    } catch (err) {
      // Activity may already be completed
      setProgress((prev) => ({ ...prev, [lessonId]: true }));
    }
  };

  const lessonsForTrack = (trackKey) => {
    const ageGroup = child?.age_group;
    return lessons
      .filter((l) => l.content?.track === trackKey)
      .filter((l) => !ageGroup || (l.age_groups || []).includes(ageGroup))
      .sort((a, b) => (a.level || 0) - (b.level || 0));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <Loader2 className="w-10 h-10 animate-spin text-purple-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-purple-950 to-slate-900 text-white">
      <header className="sticky top-0 z-30 bg-slate-900/90 backdrop-blur border-b border-purple-800/40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(`/learn/${childId}`)} className="text-white hover:bg-purple-900/50">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Music2 className="w-6 h-6 text-purple-400" />
              <div>
                <h1 className="font-bold text-xl">Music Studio</h1>
                <p className="text-xs text-purple-300">Pick an instrument and start jamming, {child?.name}!</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <Tabs value={activeTrack} onValueChange={setActiveTrack}>
          <TabsList className="grid grid-cols-4 mb-8 bg-slate-800/50">
            {TRACKS.map((track) => {
              const Icon = track.icon;
              return (
                <TabsTrigger
                  key={track.key}
                  value={track.key}
                  className="data-[state=active]:bg-purple-700 data-[state=active]:text-white"
                  data-testid={`music-track-${track.key}`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {track.name}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {TRACKS.map((track) => {
            const trackLessons = lessonsForTrack(track.key);
            const Icon = track.icon;
            const completedCount = trackLessons.filter((l) => progress[l.id]).length;

            return (
              <TabsContent key={track.key} value={track.key}>
                <div className={`p-6 rounded-2xl bg-gradient-to-r ${track.color} mb-6`}>
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                        <Icon className="w-9 h-9 text-white" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold">{track.name}</h2>
                        <p className="text-white/80">{track.desc}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-white/80">Lessons completed</p>
                      <p className="text-3xl font-extrabold">{completedCount} / {trackLessons.length}</p>
                    </div>
                  </div>
                </div>

                {trackLessons.length === 0 ? (
                  <div className="text-center py-12 text-purple-300">
                    <Music2 className="w-12 h-12 mx-auto mb-3 opacity-40" />
                    <p>No lessons yet for your age group.</p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {trackLessons.map((lesson) => {
                      const lvl = LEVEL_BADGE[lesson.level] || LEVEL_BADGE[1];
                      const isComplete = progress[lesson.id];
                      const isPlaying = playingLesson === lesson.id;
                      return (
                        <Card
                          key={lesson.id}
                          className={`bg-slate-800/60 border-purple-800/40 hover:border-purple-500 transition-all overflow-hidden ${isComplete ? 'ring-1 ring-emerald-500/50' : ''}`}
                          data-testid={`music-lesson-${lesson.id}`}
                        >
                          {isPlaying && lesson.content?.video && (
                            <div className="aspect-video bg-black relative">
                              <iframe
                                src={lesson.content.video + '?autoplay=1&rel=0'}
                                title={lesson.title}
                                className="w-full h-full"
                                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                              />
                              {lesson.content?.watch_url && (
                                <a
                                  href={lesson.content.watch_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded hover:bg-black/90"
                                  data-testid={`open-youtube-${lesson.id}`}
                                >
                                  ↗ Open on YouTube
                                </a>
                              )}
                            </div>
                          )}
                          <CardContent className="p-5">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <h3 className="font-bold text-white text-lg leading-tight">{lesson.title}</h3>
                                <p className="text-sm text-purple-200 mt-1">{lesson.description}</p>
                              </div>
                              {isComplete && <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />}
                            </div>
                            <div className="flex items-center gap-2 my-3 flex-wrap">
                              <Badge className={lvl.color}>{lvl.label}</Badge>
                              {lesson.content?.duration_min && (
                                <span className="text-xs text-purple-300 inline-flex items-center gap-1">
                                  <Clock className="w-3 h-3" /> {lesson.content.duration_min} min
                                </span>
                              )}
                              {lesson.content?.creator && (
                                <span className="text-xs text-purple-400">by {lesson.content.creator}</span>
                              )}
                            </div>
                            <div className="flex gap-2 mt-4">
                              <Button
                                size="sm"
                                className="bg-purple-600 hover:bg-purple-700"
                                onClick={() => setPlayingLesson(isPlaying ? null : lesson.id)}
                                data-testid={`play-lesson-${lesson.id}`}
                              >
                                <Play className="w-4 h-4 mr-1" />
                                {isPlaying ? 'Close' : 'Watch'}
                              </Button>
                              {!isComplete ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="bg-transparent border-purple-500 text-purple-200 hover:bg-purple-700/50 hover:text-white"
                                  onClick={() => markComplete(lesson.id)}
                                  data-testid={`complete-lesson-${lesson.id}`}
                                >
                                  <Trophy className="w-4 h-4 mr-1" />
                                  Mark Done
                                </Button>
                              ) : (
                                <Badge className="bg-emerald-900 text-emerald-300 inline-flex items-center gap-1 px-3">
                                  <CheckCircle2 className="w-3 h-3" /> Completed
                                </Badge>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </TabsContent>
            );
          })}
        </Tabs>
      </main>
    </div>
  );
};

export default MusicPage;
